import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BaseSearchForm } from '@shared/ui/base-search-form';
import { UiGroupControl } from '@shared/ui/controls/ui-group-control.model';
import { ViewControl } from '@shared/ui/controls/view-control.model';
import { UiColumn } from '@shared/ui/ui-column';
import { TextAreaControl } from '@shared/ui/controls/textarea-control.model';
import { ViewRowControl } from '@shared/ui/controls/view-row-control.model';
import { ToggleControl } from '../../../shared/ui/controls/toggle-control.model';
import { TextControl } from '../../../shared/ui/controls/text-control.model';
import { ItemSearchResult } from '@shared/interfaces/gridrows/item-search-result.interface';
import { SearchGridComponent } from '../../../shared/components/grid/search-grid.component';
import { ToolBarComponent } from '@progress/kendo-angular-toolbar';
import { ToolbarItem } from '../../../shared/interfaces/tool-bar-item.interface';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { ActivatedRoute, Router } from '@angular/router';
import { CollectionSelect } from '../../../shared/ui/select-controls/collection-select.model';
import { AuthenticationService } from '../../../core/services/authentication.serivce';
import { ReadOnlyIf, VisibleProperty } from '../../../shared/ui/control-options/control-option.interface';
import { CollectionFilter } from '../../../shared/interfaces/select-filters/collection-filter.interface';
import { Permission } from '../../../core/literals/permission.literal';
import { YesNoAllSelect } from '../../../shared/ui/select-controls/yes-no-all-select.model';
import { CountrySelect } from '../../../shared/ui/select-controls/country-select.model';
import { CountryFilter } from '../../../shared/interfaces/select-filters/country-filter.interface';

@Component({
    selector: 'app-item-list',
    templateUrl: './item-list.component.html',
    styleUrls: ['./item-list.component.css'],
    standalone: false
})
export class ItemListComponent extends BaseSearchForm<ItemSearchResult> implements OnInit {

  @ViewChild(SearchGridComponent) grid: SearchGridComponent;
  @ViewChild('toolbar') bar: ToolBarComponent;

  organizationId: Number | null;

  @Input() items: ToolbarItem[];

  ngOnInit(): void {

    var session = this.auth.getSession();

    this.organizationId = Number(session?.organizationId);

    this.toolbarItems.unshift();
  }
  constructor(private router: Router,
              private auth: AuthenticationService,
              private route: ActivatedRoute
           ) {
    super();
    this.name = "Item";
    this.apiController = "Item";
    this.keyFieldName = "id";
    this.initializeUiControls();

    this.canFilterSearch = this.auth.isAdmin();

    this.setGridSettings();
  }
  override initializeUiControls() {

    var session = this.auth.getSession();

    const view = new ViewControl("");
    const search = new UiGroupControl("", "Search");

    var collectionFilters: CollectionFilter = {
      organizationId: this.auth.getOrganizationId()
    };

    var collectionId = this.route.snapshot.queryParams['collectionId'];
    var collection_Name = this.route.snapshot.queryParams['collection_Name'];

    var homeCollection = { id: Number(collectionId), name: collection_Name };

    const collectionSelect = new CollectionSelect("collectionId", "Collection", homeCollection);
    collectionSelect.filters = collectionFilters;

    const countrySelect = new CountrySelect("countryIds", "Country", session?.countryList);

    var countryFilter: CountryFilter = {
      organizationId: this.auth.getOrganizationId()
    };

    countrySelect.filters = countryFilter;

    if (!this.auth.isAdmin()) {
      countrySelect.addOption(new ReadOnlyIf(() => {
        if (session != undefined && session?.countryList.length > 1) {
          return false;
        } else {
          return true;
        }
      }));
    }

    const product = new TextControl("product", "Product", "");
    const sku = new TextControl("sku", "SKU", "");

    const hasImage = new YesNoAllSelect("hasImage", "Has Image?", null);

    const isDraft = new ToggleControl("isDraft", "Is Draft", false);
    const isActive = new ToggleControl("isActive", "Is Active?", true);

    var row = new ViewRowControl();
    row.controls.push(collectionSelect, countrySelect, product, sku, hasImage, isActive, isDraft);

    search.rows.push(row);
    view.controls.push(search);

    this.views.push(view);

    //Only show the Is Draft check box if the user has permission to update items!
    if (!this.auth.canPerformAction(Permission.UpdateItems)) {
      hasImage.addOption(new VisibleProperty((): boolean => { return false }));
      isDraft.addOption(new VisibleProperty((): boolean => { return false }));
      isActive.addOption(new VisibleProperty((): boolean => { return false }));
    }
  }

  setGridSettings(): void {
    this.gridSettings = {
      gridName: "ItemSearch_Grid",
      showSubGrid: true,
      subGridColumns: this.getSubGridColumns(),
      subGridDataProperty: "componentsList",
      preloadData: true,
      columns: this.getColumns(),
      doubleClick: () => { this.doubleClick(window.event as MouseEvent) }
    };
  }

  private getSubGridColumns(): UiColumn[] {
    let cols: UiColumn[] = [];

    var component = new UiColumn("component", "Component", 160);
    var componentCnt = new UiColumn("componentCnt", "Quantity", 160);

    cols.push(component, componentCnt);

    return cols;
  }

  private doubleClick(e: MouseEvent): void {
    if (this.gridSettings.selectedRow) {
      var td = e.target as HTMLTableCellElement;
      var tableBody = document.querySelector("#" + this.gridSettings.gridName + " tbody");
      var canContinue: boolean = false;

      if (td && tableBody) {
        canContinue = tableBody.contains(td);
      }

      //If the user can update the item detail page, take them there!
      //Else, redirect them to the add to cart page
      var redirectToDetailPage = this.auth.canPerformAction(Permission.UpdateItems);

      if (canContinue) {
        var entityID = this.gridSettings.selectedRow["id"];
        if (entityID) {
          if (redirectToDetailPage) {
            var endpoint: string = "/Item/Detail/" + entityID;
            var baseUrl = window.location.href.replace(this.router.url, '');
            window.open(baseUrl + endpoint, '_self');
          } else {
            var endpoint: string = "/Item/AddToCart/" + entityID;
            var baseUrl = window.location.href.replace(this.router.url, '');
            window.open(baseUrl + endpoint, '_self');
          }
        }
      }
    }
  }

  private getColumns(): UiColumn[] {
    let cols: UiColumn[] = [];

    var title = new UiColumn("title", "Product", 100);
    var country = new UiColumn("country_Name", "Country", 100);
    var inventoryCount = new UiColumn("inventoryCount", "Inventory", 100);
    var sku = new UiColumn("sku", "SKU", 100);
    var condition = new UiColumn("condition", "Condition", 100);
    var isDraft = new UiColumn("isDraft", "Is Draft?", 100, true, "boolean");
    var collection_Name = new UiColumn("collectionList", "Collection", 100);

    cols.push(title, condition, country, sku, collection_Name, inventoryCount, isDraft);

    if (!this.auth.canPerformAction(Permission.UpdateItems)) {
      var removeIsDraftColumn = cols.findIndex(x => x.field == "isDraft");
      cols.splice(removeIsDraftColumn, 1);
    }

    if (!this.auth.isAdmin()) {
      var removeCollectionColumn = cols.findIndex(x => x.field == "collectionList");
      cols.splice(removeCollectionColumn, 1);
    }

    return cols;
  }
}
